// Il triage con PM e Team Leader (tela, artboard 32): a sinistra la conversazione con i loro
// pops, a destra i requisiti che si compilano a ogni risposta. È una sessione del progetto: i
// token vanno sul progetto e, mentre aspetta, la Dashboard la mostra fra «Aspettano te».
import { useState } from 'react';
import { Accordion } from '../../../components/Accordion';
import { Banner } from '../../../components/Banner';
import { Button } from '../../../components/Button';
import { Field, TextArea } from '../../../components/Field';
import { Pop } from '../../../components/Pop';
import type { AgentProfile, ConnectedProvider, TriageTurn } from '../../../lib/api';
import { modelLabel } from '../../agents/AgentEditor';
import { ModelPicker, locate, type ModelValue } from '../../agents/ModelPicker';

export interface TriageState {
  sessionId: string | null;
  lines: { author: string; profile: string; text: string; me?: boolean }[];
  sections: TriageTurn['sections'];
  done: boolean;
  /** modello e ragionamento per PM e Team leader: senza modello, quelli del loro pop */
  models: { lead: ModelValue; technical: ModelValue };
}

const OWN: ModelValue = { model: null, effort: null, thinking: true };

export const EMPTY_TRIAGE: TriageState = {
  sessionId: null,
  lines: [],
  sections: {},
  done: false,
  models: { lead: OWN, technical: OWN },
};

/** «Claude Code · opus · ragionamento alto»: il modello come lo legge una persona */
function described(providers: ConnectedProvider[], model: string | null, effort: string | null) {
  if (!model) return 'nessun modello: collega un provider';
  const found = locate(providers, model);
  const name = found ? `${found.provider.name} · ${found.entry.label}` : modelLabel(model);
  return effort ? `${name} · ragionamento ${effort}` : name;
}

const TONE: Record<string, string> = {
  chiaro: 'ok',
  'da chiedere': 'warn',
  'da scrivere': 'neutral',
};

export function Triage({
  state,
  onState,
  requirements,
  onRequirements,
  docs,
  onDocs,
  profiles,
  providers,
  roles,
  run,
}: {
  state: TriageState;
  onState: (s: TriageState) => void;
  requirements: string;
  onRequirements: (v: string) => void;
  docs: string;
  onDocs: (v: string) => void;
  profiles: AgentProfile[];
  providers: ConnectedProvider[];
  /** i pop che conducono il triage, come li sceglie il server */
  roles: { lead: string; technical: string };
  /** Un turno: crea il progetto se serve e chiede a PM e Team Leader. */
  run: (reply: string | null) => Promise<TriageTurn>;
}) {
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const avatar = (profile: string) => profiles.find((p) => p.id === profile)?.avatar ?? null;
  // i pop che il server fa parlare nel triage (agents.yaml › triage), non il coordinatore
  const pops = [
    { key: 'lead' as const, title: 'PM', pop: profiles.find((p) => p.id === roles.lead) },
    {
      key: 'technical' as const,
      title: 'Team leader',
      pop: profiles.find((p) => p.id === roles.technical),
    },
  ];
  const setModel = (key: 'lead' | 'technical', v: ModelValue) =>
    onState({ ...state, models: { ...state.models, [key]: v } });
  // il modello che userà davvero: quello scelto qui, o quello del pop
  const using = (key: 'lead' | 'technical', pop: AgentProfile | undefined) => {
    const v = state.models[key];
    return v.model
      ? { text: described(providers, v.model, v.effort), custom: true }
      : {
          text: described(providers, pop?.effectiveModel ?? null, pop?.effort ?? null),
          custom: false,
        };
  };

  const send = async (text: string | null) => {
    setBusy(true);
    setError(null);
    const mine = text ? [{ author: 'Tu', profile: '', text, me: true }] : [];
    const lines = [...state.lines, ...mine];
    onState({ ...state, lines });
    try {
      const t = await run(text);
      if (!t.ok) setError(t.error ?? 'PM e Team Leader non hanno risposto.');
      onRequirements(t.requirements);
      onState({
        ...state,
        sessionId: t.sessionId,
        lines: [...lines, ...t.messages],
        sections: t.sections,
        done: t.done,
      });
      setReply('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Il triage non è partito: riprova.');
    } finally {
      setBusy(false);
    }
  };

  const sections = Object.entries(state.sections);
  return (
    <div className="np-triage">
      <section className="np-card">
        <header className="np-card-head">
          <h2 className="t-overline">Triage con PM e Team leader</h2>
        </header>
        <div className="np-two">
          {pops.map(({ key, title, pop }) => {
            const u = using(key, pop);
            return (
              <div key={key} className="np-triage-pop">
                {pop && <Pop avatar={pop.avatar} size={44} />}
                <div style={{ minWidth: 0 }}>
                  <div className="t-strong">
                    {pop ? `${pop.nickname || pop.label} · ${title}` : title}
                  </div>
                  <div className="t-caption dim" title={u.text}>
                    {u.text}
                    {u.custom ? ' (personalizzato)' : ''}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <Accordion
          items={[
            {
              id: 'models',
              title: 'Personalizza i modelli',
              body: (
                <div style={{ display: 'grid', gap: 12 }}>
                  {pops.map(({ key, title, pop }) => (
                    <ModelPicker
                      key={key}
                      providers={providers}
                      title={`Modello del ${title}`}
                      subtitle="solo per questo triage"
                      value={state.models[key]}
                      onChange={(v) => setModel(key, v)}
                      follows={{
                        choice: {
                          model: pop?.model ?? null,
                          effort: pop?.effort ?? null,
                          thinking: pop?.thinking ?? true,
                        },
                        resolved: pop?.effectiveModel ?? null,
                      }}
                      followsName="Quello del pop"
                      followsSource={pop ? `il pop ${pop.nickname || pop.label}` : 'il pop'}
                      followsFrom="dal pop"
                    />
                  ))}
                </div>
              ),
            },
          ]}
        />
        {state.lines.length === 0 ? (
          <>
            <Field
              label="Docs del progetto"
              hint="Incolla brief, README o appunti: PM e Team leader partono da qui."
            >
              <TextArea value={docs} onChange={onDocs} rows={6} />
            </Field>
            <div>
              <Button variant="primary" icon="play" loading={busy} onClick={() => void send(null)}>
                Inizia il triage
              </Button>
            </div>
          </>
        ) : (
          <div className="np-chat" aria-live="polite">
            {state.lines.map((l, i) =>
              l.me ? (
                <div key={i} className="np-bubble np-bubble-me">
                  {l.text}
                </div>
              ) : (
                <div key={i} className="np-bubble-row">
                  {avatar(l.profile) && <Pop avatar={avatar(l.profile) as string} size={40} />}
                  <div style={{ minWidth: 0 }}>
                    <div className="t-caption faint">{l.author}</div>
                    <div className="np-bubble">{l.text}</div>
                  </div>
                </div>
              ),
            )}
          </div>
        )}
        {error && <Banner kind="danger">{error}</Banner>}
        {state.lines.length > 0 && !state.done && (
          <div className="np-reply">
            <TextArea value={reply} onChange={setReply} rows={2} placeholder="Rispondi…" />
            <Button
              variant="primary"
              loading={busy}
              disabled={!reply.trim()}
              onClick={() => void send(reply.trim())}
            >
              Invia
            </Button>
          </div>
        )}
        {state.done && (
          <Banner kind="info">
            Requisiti completi: puoi andare avanti, o correggerli a destra.
          </Banner>
        )}
      </section>
      <section className="np-card">
        <header className="np-card-head">
          <h2 className="t-overline">Requisiti in costruzione</h2>
          {sections.length > 0 && (
            <span className="t-caption faint" style={{ marginLeft: 'auto' }}>
              {sections.filter(([, v]) => v === 'chiaro').length} di {sections.length}
            </span>
          )}
        </header>
        {sections.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {sections.map(([name, v]) => (
              <span
                key={name}
                className="dash-pill"
                style={{ color: `var(--${TONE[v]})`, background: `var(--${TONE[v]}-soft)` }}
              >
                {name} · {v}
              </span>
            ))}
          </div>
        )}
        <TextArea value={requirements} onChange={onRequirements} rows={16} mono />
      </section>
    </div>
  );
}
